import { useRef, useState, useEffect, useCallback } from 'react';
import jsQR from 'jsqr';
import { SwitchCamera, Keyboard } from 'lucide-react';

interface ScanCameraProps {
  onScan: (sessionId: string) => void;
}

export function ScanCamera({ onScan }: ScanCameraProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [manualMode, setManualMode] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const scanningRef = useRef(true);

  const startCamera = useCallback(async () => {
    setError(null);
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      setError('No se pudo acceder a la cámara. Usa el modo manual.');
    }
  }, [facingMode]);

  useEffect(() => {
    if (!manualMode) {
      startCamera();
    }
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [startCamera, manualMode]);

  useEffect(() => {
    if (manualMode) return;
    scanningRef.current = true;

    const scan = () => {
      if (!scanningRef.current) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
        requestAnimationFrame(scan);
        return;
      }
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });
      if (code?.data) {
        const extracted = extractSessionId(code.data);
        if (extracted) {
          scanningRef.current = false;
          onScan(extracted);
          return;
        }
      }
      requestAnimationFrame(scan);
    };

    requestAnimationFrame(scan);
  }, [manualMode, onScan]);

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const extracted = extractSessionId(manualCode.trim());
    if (extracted) {
      onScan(extracted);
    } else {
      setError('Código no válido');
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 w-full animate-fade-in">
      {manualMode ? (
        <form onSubmit={handleManualSubmit} className="flex flex-col gap-3 w-full max-w-sm">
          <label className="text-sm font-medium" style={{ color: 'var(--qr-text-secondary)' }}>
            Código público o URL del QR
          </label>
          <input
            type="text"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Ej: ABC123 o https://..."
            className="w-full px-4 py-3 rounded-xl outline-none"
            style={{
              background: 'var(--qr-surface)',
              border: '1px solid var(--qr-border)',
              color: 'var(--qr-text)',
            }}
          />
          <button
            type="submit"
            className="w-full py-3 rounded-xl font-semibold transition-transform duration-200 active:scale-[0.98]"
            style={{ background: 'var(--qr-primary)', color: '#fff' }}
          >
            Buscar
          </button>
        </form>
      ) : (
        <div className="relative w-full max-w-sm aspect-[3/4] rounded-2xl overflow-hidden">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            muted
            playsInline
          />
          <canvas ref={canvasRef} className="hidden" />

          {/* Corner overlays */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-4 left-4 w-8 h-8 border-l-4 border-t-4 rounded-tl-lg animate-scan-pulse" style={{ borderColor: 'var(--qr-primary)' }} />
            <div className="absolute top-4 right-4 w-8 h-8 border-r-4 border-t-4 rounded-tr-lg animate-scan-pulse" style={{ borderColor: 'var(--qr-primary)' }} />
            <div className="absolute bottom-4 left-4 w-8 h-8 border-l-4 border-b-4 rounded-bl-lg animate-scan-pulse" style={{ borderColor: 'var(--qr-primary)' }} />
            <div className="absolute bottom-4 right-4 w-8 h-8 border-r-4 border-b-4 rounded-br-lg animate-scan-pulse" style={{ borderColor: 'var(--qr-primary)' }} />
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-center px-4" style={{ color: 'var(--qr-danger)' }}>
          {error}
        </p>
      )}

      <div className="flex gap-3">
        {!manualMode && (
          <button
            onClick={toggleCamera}
            className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-transform duration-200 active:scale-[0.98]"
            style={{
              background: 'var(--qr-surface)',
              border: '1px solid var(--qr-border)',
              color: 'var(--qr-text)',
            }}
          >
            <SwitchCamera size={18} />
            Alternar cámara
          </button>
        )}
        <button
          onClick={() => {
            setManualMode((m) => !m);
            setError(null);
          }}
          className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-transform duration-200 active:scale-[0.98]"
          style={{
            background: 'var(--qr-surface)',
            border: '1px solid var(--qr-border)',
            color: 'var(--qr-text)',
          }}
        >
          <Keyboard size={18} />
          {manualMode ? 'Usar cámara' : 'Manual'}
        </button>
      </div>
    </div>
  );
}

function extractSessionId(raw: string): string | null {
  const trimmed = raw.trim();
  // Match /s/{id} from URL
  const match = trimmed.match(/\/s\/([^\/\s]+)/);
  if (match) return match[1];
  // Raw UUID or public_id (alphanumeric)
  if (/^[A-Z0-9]{6}$/i.test(trimmed) || /^[0-9a-f-]{36}$/i.test(trimmed)) {
    return trimmed;
  }
  return null;
}
